import { useEffect, useMemo, useState, type FormEvent, type ReactNode } from 'react';
import {
  api,
  type BaseURLPlan,
  type DomainPurpose,
  type DomainValidationResult,
  type EnvironmentDomain,
  type FrontendMagentoBinding,
  type MagentoTopologyView,
  type ProjectApplication,
  type RoutingInspection,
  type SetEnvironmentDomainInput,
  type SetFrontendBindingInput,
  type SiteHealthEntry,
  type TLSMode,
  type TopologyChange,
  type TopologyDiff,
  type TopologyDiscovery,
  type TopologyFrontendView,
  type TopologyInternalNetwork,
  type TopologyWebsiteView,
} from '../api';
import { useResource } from '../hooks';
import {
  dnsStatusLabel,
  domainPurposeDescription,
  domainPurposeLabel,
  
  ingressOwnerLabel,
  labelFor,
  redirectCheckLabel,
  routeDestinationLabel,
  
  tlsObservedLabel,
  topologyChangeLabel,
} from '../labels';
import { ConfirmDialog, ErrorNotice, Field, LoadingRows, StatusBadge } from './ui';
import { CopyButton } from './CopyButton';
import { CertificateAutomation, CertificateCell, DnsRecords, hasRenewableCertificate, needsCertificate } from './DomainCertificates';
import { Callout, Checkbox, Dialog, Input, RowActions, Section, Select, Table } from './primitives';
import { domainProblems, websiteStatus } from './topologyHealth';

/**
 * MagentoTopologyCard is the environment's storefront map (CO-46): one
 * Magento backend, its websites, and — per website — the store views, the
 * public hostnames and the frontend deployments that serve them, each with
 * its own health. Technical detail (codes, routing, endpoints, scopes)
 * sits behind disclosure so a non-technical reader sees "MyTherm ·
 * mytherm.ro · failing TLS" and a technical one can open the store view
 * codes and MAGE_RUN mapping underneath.
 */
export function MagentoTopologyCard({
  environmentId,
  applications,
  onExecutionStarted,
}: {
  environmentId: string;
  applications: ProjectApplication[];
  onExecutionStarted?: (executionId: string) => void;
}) {
  const [reloadToken, setReloadToken] = useState(0);
  const topology = useResource(() => api.getMagentoTopology(environmentId), [environmentId, reloadToken], undefined, { key: `env:${environmentId}:magento-topology` });
  const [busy, setBusy] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [actionError, setActionError] = useState<Error | null>(null);
  const [dialogError, setDialogError] = useState<Error | null>(null);
  const [discovery, setDiscovery] = useState<TopologyDiscovery | null>(null);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [domainDialog, setDomainDialog] = useState<{ open: boolean; domain?: EnvironmentDomain; websiteId?: string }>({ open: false });
  const [bindingDialog, setBindingDialog] = useState<{ open: boolean; frontend?: TopologyFrontendView; websiteId?: string }>({ open: false });
  const [deleteDomain, setDeleteDomain] = useState<EnvironmentDomain | null>(null);
  const [validation, setValidation] = useState<DomainValidationResult[] | null>(null);

  const view = topology.data?.topology ?? null;
  const frontends = useMemo(() => applications.filter((a) => a.type === 'frontend'), [applications]);
  const reload = () => setReloadToken((v) => v + 1);

  // inDialog routes the failure into the open dialog instead of the card,
  // so the person sees it next to the form they are editing.
  async function run(key: string, action: () => Promise<unknown>, success?: string, inDialog = false) {
    setBusy(key);
    setActionError(null);
    setNotice(null);
    try {
      const result = await action();
      if (success) setNotice(success);
      reload();
      return result;
    } catch (cause) {
      const err = cause instanceof Error ? cause : new Error(String(cause));
      if (inDialog) setDialogError(err);
      else setActionError(err);
      return null;
    } finally {
      setBusy(null);
    }
  }

  async function discover() {
    setBusy('discover');
    setActionError(null);
    setNotice(null);
    try {
      const res = await api.discoverMagentoTopology(environmentId);
      setDiscovery(res.discovery);
      if (res.discovery.diff.in_sync) {
        setNotice(`Magento reports ${res.discovery.diff.websites_discovered} website(s); the record is up to date.`);
        reload();
      } else {
        setReviewOpen(true);
      }
    } catch (cause) {
      setActionError(cause instanceof Error ? cause : new Error(String(cause)));
    } finally {
      setBusy(null);
    }
  }

  async function importDiscovery(snapshotId?: string) {
    const res = await run('import', () => api.importMagentoTopology(environmentId, snapshotId), 'Magento websites imported. Bind hostnames and frontends to any new website.');
    if (res) {
      setReviewOpen(false);
      setDiscovery(null);
    }
  }

  const health = view?.health ?? null;
  const backendStatus = health?.backend.status ?? 'unknown';
  // Hostnames of the installation itself (API, Admin, media) are shown on
  // the backend; only storefront hostnames can be "not attached".
  const isInstallationDomain = (d: EnvironmentDomain) => d.purpose === 'backend_api' || d.purpose === 'admin' || d.purpose === 'media' || (d.purpose === '' && (d.role === 'api' || d.role === 'media' || d.role === 'admin'));
  const installationDomains = (view?.unbound_domains ?? []).filter(isInstallationDomain);
  const unboundDomains = (view?.unbound_domains ?? []).filter((d) => !isInstallationDomain(d));
  const drift = view?.drift && !view.drift.in_sync ? view.drift : null;
  const websites = view?.websites ?? [];
  const summary = view
    ? `${websites.length === 0 ? 'No websites discovered yet' : `${websites.length} website${websites.length === 1 ? '' : 's'}`}${view.multi_website ? ' · unknown hostnames are refused' : ''}`
    : '';

  return (
    <Section
      id={`topology-${environmentId}`}
      className="topology"
      testId="magento-topology"
      title="Storefronts"
      description={summary}
      actions={
        <>
          <button type="button" className="button button--secondary" onClick={discover} disabled={busy !== null || !view?.application_id} data-testid="topology-discover">
            {busy === 'discover' ? 'Asking Magento' : 'Discover in Magento'}
          </button>
          <button type="button" className="button button--secondary" onClick={() => setDomainDialog({ open: true })} disabled={busy !== null || !view}>
            Add hostname
          </button>
        </>
      }
    >
      {notice ? <Callout tone="success">{notice}</Callout> : null}
      <ErrorNotice error={actionError} />
      {topology.error ? <ErrorNotice error={topology.error} /> : null}
      {topology.loading && !view ? <LoadingRows label="Loading storefronts…" rows={3} rowHeight="44px" /> : null}

      {drift ? (
        <Callout
          tone={drift.level === 'critical' ? 'danger' : 'warning'}
          role="status"
          title="Magento differs from what Commerce Ops has on record"
          testId="topology-drift"
          actions={
            <button type="button" className="button button--secondary button--small" onClick={() => { setDiscovery(null); setReviewOpen(true); }}>
              Review and import
            </button>
          }
        >
          {drift.changes.length} change{drift.changes.length === 1 ? '' : 's'}
          {view?.last_discovery ? ` found ${ago(view.last_discovery.discovered_at)}` : ''}. Nothing was changed automatically.
        </Callout>
      ) : null}

      {view ? (
        <ol className="topology__tree" aria-label="Magento installation">
          <li className={`topology__node topology__node--backend topology__node--${levelOf(backendStatus)}`}>
            <span className="topology__mark" aria-hidden="true" />
            <div className="topology__main">
              <strong>Magento backend</strong>
              <span className="muted">
                {' '}
                {String(view.installation.backend_api_url || view.installation.secure_base_url || view.installation.base_url || '')
                  .replace(/^https?:\/\//, '')
                  .replace(/\/graphql$/, '')
                  .replace(/\/$/, '') || 'API hostname not set'}
                {view.installation.active_release && typeof view.installation.active_release === 'object'
                  ? ` · release ${(view.installation.active_release as { release_tag?: string }).release_tag ?? ''}`
                  : ''}
              </span>
              {installationDomains.length > 0 ? (
                <dl className="topology__facts">
                  <dt>Hostname{installationDomains.length === 1 ? '' : 's'}</dt>
                  <dd>
                    {installationDomains.map((d) => (
                      <span key={d.id} className="topology__host">
                        {d.hostname}
                        <span className="muted topology__role"> ({labelFor(domainPurposeLabel, d.purpose || d.role)})</span>
                        <DomainState domain={d} />
                      </span>
                    ))}
                  </dd>
                </dl>
              ) : null}
            </div>
            <HealthBadge status={backendStatus} detail={health?.backend.detail} />
          </li>
          {websites.map((w) => (
            <WebsiteRow
              key={w.website.id}
              website={w}
              frontends={frontends}
              onAddHostname={() => setDomainDialog({ open: true, websiteId: w.website.id })}
              onEditHostname={(d) => setDomainDialog({ open: true, domain: d })}
              onDeleteHostname={(d) => setDeleteDomain(d)}
              onBindFrontend={(fe) => setBindingDialog({ open: true, frontend: fe, websiteId: w.website.id })}
            />
          ))}
          {websites.length === 0 && !topology.loading ? (
            <li className="topology__node topology__node--empty">
              <span className="topology__mark topology__mark--dashed" aria-hidden="true" />
              <div className="topology__main">
                <strong>No websites on record</strong>
              </div>
            </li>
          ) : null}
        </ol>
      ) : null}

      {view && (unboundDomains.length > 0 || view.unbound_frontends.length > 0) ? (
        <div className="topology__unbound">
          <h3>Not attached to a website</h3>
          <ul className="topology__list">
            {unboundDomains.map((d) => (
              <li key={d.id}>
                <DomainLine domain={d} websites={websites} onEdit={() => setDomainDialog({ open: true, domain: d })} onDelete={() => setDeleteDomain(d)} />
              </li>
            ))}
            {view.unbound_frontends.map((fe) => (
              <li key={fe.application.id} className="topology__frontend-line">
                <span className="topology__glyph" aria-hidden="true">▣</span>
                <span>
                  <strong>{fe.application.name}</strong>
                  <span className="muted"> frontend deployment · no Magento website bound</span>
                </span>
                <button type="button" className="button button--secondary button--small" onClick={() => setBindingDialog({ open: true, frontend: fe })}>
                  Bind to a website
                </button>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {view && (view.routing.issues.length > 0) ? (
        <ul className="topology__issues" aria-label="Routing problems">
          {view.routing.issues.map((i, idx) => (
            <li key={`${i.code}-${idx}`} className={`topology__issue topology__issue--${i.severity}`}>
              <strong>{i.hostname ? `${i.hostname}: ` : ''}</strong>
              {i.message}
            </li>
          ))}
        </ul>
      ) : null}

      {view ? (
        <div className="topology__sections">
          <DomainsSection
            view={view}
            environmentId={environmentId}
            reloadToken={reloadToken}
            validation={validation}
            busy={busy}
            onValidate={async () => {
              const res = (await run('validate', () => api.validateEnvironmentDomains(environmentId))) as { results: DomainValidationResult[] } | null;
              if (res) setValidation(res.results);
            }}
            onRequestCertificate={() => run('certificate', async () => {
              const res = await api.issueEnvironmentCertificate(environmentId);
              onExecutionStarted?.(res.execution.id);
            }, 'Certificate request started as an execution.')}
            onRenewCertificate={() => run('certificate', async () => {
              const res = await api.renewEnvironmentCertificate(environmentId);
              onExecutionStarted?.(res.execution.id);
            }, 'Certificate renewal started as an execution.')}
            onEdit={(d) => setDomainDialog({ open: true, domain: d })}
            onDelete={(d) => setDeleteDomain(d)}
          />
          {view.internal_network ? <InternalNetworkSection network={view.internal_network} /> : null}
          <FrontendsSection view={view} environmentId={environmentId} onEdit={(fe) => setBindingDialog({ open: true, frontend: fe, websiteId: fe.binding?.website_id })} />
          <BaseURLSection environmentId={environmentId} reloadToken={reloadToken} busy={busy} onApply={(plan) => run('base-urls', async () => {
            const res = await api.applyMagentoBaseURLs(environmentId, plan);
            onExecutionStarted?.(res.execution.id);
          }, 'Base URL changes started as an execution.')} />
          <RoutingSection environmentId={environmentId} reloadToken={reloadToken} busy={busy} onApply={() => run('routing', async () => {
            const res = await api.applyStorefrontRouting(environmentId);
            onExecutionStarted?.(res.execution.id);
          }, 'Routing changes started as an execution.')} />
        </div>
      ) : null}

      <DriftReviewDialog
        open={reviewOpen}
        discovery={discovery}
        drift={discovery?.diff ?? view?.drift ?? null}
        busy={busy === 'import'}
        onImport={() => importDiscovery(discovery?.snapshot.id)}
        onCancel={() => setReviewOpen(false)}
      />
      {domainDialog.open && view ? (
        <DomainDialog
          view={view}
          frontends={frontends}
          domain={domainDialog.domain}
          websiteId={domainDialog.websiteId}
          busy={busy === 'domain'}
          error={dialogError}
          onClose={() => { setDomainDialog({ open: false }); setDialogError(null); }}
          onSave={async (input) => {
            setDialogError(null);
            const ok = await run('domain', () => api.setEnvironmentDomain(environmentId, input), `Hostname ${input.hostname} saved. Apply routing to make the server answer it.`, true);
            if (ok) setDomainDialog({ open: false });
          }}
        />
      ) : null}
      {bindingDialog.open && view && bindingDialog.frontend ? (
        <FrontendBindingDialog
          view={view}
          frontend={bindingDialog.frontend}
          websiteId={bindingDialog.websiteId}
          busy={busy === 'binding'}
          error={dialogError}
          onClose={() => { setBindingDialog({ open: false }); setDialogError(null); }}
          onSave={async (input) => {
            setDialogError(null);
            const ok = await run('binding', () => api.setFrontendBinding(environmentId, input), `${bindingDialog.frontend?.application.name} bound to its Magento website.`, true);
            if (ok) setBindingDialog({ open: false });
          }}
        />
      ) : null}
      <ConfirmDialog
        open={deleteDomain !== null}
        title={deleteDomain ? `Remove ${deleteDomain.hostname}?` : 'Remove hostname'}
        description={
          deleteDomain
            ? `The hostname record is removed from Commerce Ops now. The server keeps answering ${deleteDomain.hostname} until the gate configuration is applied again: the next deployment of this environment or the next certificate request. From then on it stops answering${
                deleteDomain.canonical ? ` — and it is the main hostname of its storefront, so visitors using it get no store` : ''
              }. DNS and Magento's own configuration are not touched.`
            : ''
        }
        // A storefront's main hostname asks for its name (UX-17).
        confirmText={deleteDomain?.canonical ? deleteDomain.hostname : undefined}
        confirmLabel="Remove hostname"
        danger
        busy={busy === 'delete-domain'}
        onConfirm={async () => {
          if (!deleteDomain) return;
          const ok = await run('delete-domain', () => api.deleteEnvironmentDomain(environmentId, deleteDomain.id), `${deleteDomain.hostname} removed.`);
          if (ok) setDeleteDomain(null);
        }}
        onCancel={() => setDeleteDomain(null)}
      />
    </Section>
  );
}

// ---------------------------------------------------------------------------
// Website row
// ---------------------------------------------------------------------------

function WebsiteRow({
  website,
  frontends,
  onAddHostname,
  onEditHostname,
  onDeleteHostname,
  onBindFrontend,
}: {
  website: TopologyWebsiteView;
  frontends: ProjectApplication[];
  onAddHostname: () => void;
  onEditHostname: (d: EnvironmentDomain) => void;
  onDeleteHostname: (d: EnvironmentDomain) => void;
  onBindFrontend: (fe: TopologyFrontendView) => void;
}) {
  const w = website.website;
  // Site checks and hostname observations combined (topologyHealth.ts).
  const status = websiteStatus(website);
  const storefronts = website.domains.filter((d) => d.canonical && (d.purpose === 'magento_storefront' || d.purpose === 'headless_storefront' || d.purpose === ''));
  const aliases = website.domains.filter((d) => d.purpose === 'alias' || d.role === 'alias');
  const activeViews = website.store_views.filter((v) => v.status === 'active');
  const removed = w.status === 'removed';
  return (
    <li className={`topology__node topology__node--website topology__node--${removed ? 'removed' : levelOf(status)}`} data-testid={`topology-website-${w.code}`}>
      <span className="topology__mark" aria-hidden="true" />
      <div className="topology__main">
        <div className="topology__title">
          <strong>{w.name || w.code}</strong>
          {w.is_default ? <span className="badge badge--neutral">Default website</span> : null}
          {removed ? <StatusBadge kind="topologyItem" value="removed" /> : null}
          {w.source === 'migrated' ? <span className="badge badge--neutral" title="Recorded by the upgrade before Magento was asked; run Discover in Magento to confirm the codes.">Assumed</span> : null}
        </div>
        <dl className="topology__facts">
          <dt>Hostname</dt>
          <dd>
            {storefronts.length === 0 ? (
              <span className="muted">none yet</span>
            ) : (
              storefronts.map((d) => (
                <span key={d.id} className="topology__host">
                  {d.hostname}
                  <DomainState domain={d} />
                </span>
              ))
            )}
            {aliases.length > 0 ? <span className="muted"> · redirects from {aliases.map((a) => a.hostname).join(', ')}</span> : null}
          </dd>
          <dt>Store view{activeViews.length === 1 ? '' : 's'}</dt>
          <dd>
            {activeViews.length === 0 ? <span className="muted">none</span> : activeViews.map((v) => (
              <span key={v.id} className="topology__chip" title={`${v.name} · ${v.locale || 'locale unknown'}`}>
                <code>{v.code}</code>
                {!v.is_active ? <span className="muted"> (disabled)</span> : null}
              </span>
            ))}
          </dd>
          <dt>Frontend</dt>
          <dd>
            {website.frontends.length === 0 ? (
              <span className="muted">{storefronts.some((d) => d.purpose === 'magento_storefront') ? 'Magento renders this shop' : 'none bound'}</span>
            ) : (
              website.frontends.map((fe) => (
                <span key={fe.application.id} className="topology__chip">
                  {fe.application.name}
                  {fe.binding?.store_view_id ? (
                    <span className="muted"> → <code>{website.store_views.find((v) => v.id === fe.binding?.store_view_id)?.code ?? 'store view'}</code></span>
                  ) : null}
                </span>
              ))
            )}
          </dd>
        </dl>
        <details className="topology__details">
          <summary>Technical details</summary>
          <div className="topology__detail-grid">
            <dl className="definition-list">
              <dt className="muted">Website code</dt>
              <dd><code>{w.code}</code>{w.magento_website_id != null ? <span className="muted"> · id {w.magento_website_id}</span> : null}</dd>
              {website.stores.map((st) => (
                <div key={st.id}>
                  <dt className="muted">Store</dt>
                  <dd>
                    <code>{st.code || `#${st.magento_group_id ?? '?'}`}</code>
                    {st.name ? ` ${st.name}` : ''}
                    {st.root_category_id != null ? <span className="muted"> · root category {st.root_category_id}</span> : null}
                  </dd>
                </div>
              ))}
              {website.store_views.map((v) => (
                <div key={v.id}>
                  <dt className="muted">Store view</dt>
                  <dd>
                    <code>{v.code}</code> {v.name}
                    {v.locale ? <span className="muted"> · {v.locale}</span> : null}
                    {v.display_currency ? <span className="muted"> · {v.display_currency}</span> : null}
                    {!v.is_active ? <> <StatusBadge kind="topologyItem" value="disabled" /></> : null}
                    {v.status === 'removed' ? <> <StatusBadge kind="topologyItem" value="removed" /></> : null}
                    {v.base_url ? (
                      <div className="table__sub">
                        base URL {v.base_url}
                        {v.base_url_scope ? ` (${v.base_url_scope === 'default' ? 'default scope' : `${v.base_url_scope} scope${v.base_url_scope_code ? `, ${v.base_url_scope_code}` : ''}`})` : ''}
                      </div>
                    ) : null}
                  </dd>
                </div>
              ))}
            </dl>
            <div>
              <h4 className="topology__h4">Routing</h4>
              {website.routes.length === 0 ? (
                <p className="topology__text">No hostname routed yet.</p>
              ) : (
                <ul className="topology__list">
                  {website.routes.map((r) => (
                    <li key={r.domain_id}>
                      <code>{r.hostname}</code> → {labelFor(routeDestinationLabel, r.destination)}
                      {r.run_code ? <span className="muted"> · MAGE_RUN_TYPE={r.run_type} MAGE_RUN_CODE={r.run_code}</span> : null}
                      {r.frontend_service ? <span className="muted"> · service {r.frontend_service}</span> : null}
                      {r.redirect_to ? <span className="muted"> · 301 to {r.redirect_to}</span> : null}
                    </li>
                  ))}
                </ul>
              )}
              {website.frontends.map((fe) => (
                <div key={fe.application.id} className="topology__frontend">
                  <h4 className="topology__h4">{fe.application.name}</h4>
                  <ul className="topology__list">
                    <li>GraphQL endpoint: <code>{fe.binding?.graphql_endpoint || 'not set'}</code></li>
                    <li>Store header: <code>{website.store_views.find((v) => v.id === fe.binding?.store_view_id)?.code ?? 'not set'}</code></li>
                    <li>Public URL: <code>{fe.binding?.public_base_url || fe.domains[0]?.hostname || 'not set'}</code></li>
                    {fe.service_name ? <li>Compose service: <code>{fe.service_name}</code></li> : null}
                  </ul>
                </div>
              ))}
              {website.health && website.health.sites.length > 0 ? (
                <>
                  <h4 className="topology__h4">Monitors</h4>
                  <ul className="topology__list">
                    {website.health.sites.map((s, i) => (
                      <li key={`${s.label}-${i}`}>
                        <HealthBadge status={s.status} small /> {s.label}
                      </li>
                    ))}
                  </ul>
                </>
              ) : null}
            </div>
          </div>
          <div className="pill-list topology__pills">
            <button type="button" className="button button--secondary button--small" onClick={onAddHostname}>Add hostname</button>
            {frontends.length > 0 ? (
              <button type="button" className="button button--secondary button--small" onClick={() => onBindFrontend(website.frontends[0] ?? { application: frontends[0]!, domains: [] })}>
                {website.frontends.length > 0 ? 'Edit frontend binding' : 'Bind a frontend'}
              </button>
            ) : null}
            {website.domains.map((d) => (
              <span key={d.id} className="pill-list__group">
                <button type="button" className="button button--ghost button--small" onClick={() => onEditHostname(d)}>Edit {d.hostname}</button>
                <button type="button" className="button button--danger-quiet button--small" onClick={() => onDeleteHostname(d)} aria-label={`Remove ${d.hostname}`}>Remove</button>
              </span>
            ))}
          </div>
        </details>
      </div>
      <HealthBadge status={status} detail={removed ? 'Magento no longer reports this website' : undefined} />
    </li>
  );
}

function DomainState({ domain }: { domain: EnvironmentDomain }) {
  const parts = domainProblems(domain, { dns: (v) => labelFor(dnsStatusLabel, v), tls: (v) => labelFor(tlsObservedLabel, v) });
  if (parts.length === 0) return null;
  return (
    <>
      {' '}
      <StatusBadge kind="siteHealth" value="critical" label={parts.join(' · ')} />
    </>
  );
}

function DomainLine({ domain, websites, onEdit, onDelete }: { domain: EnvironmentDomain; websites: TopologyWebsiteView[]; onEdit: () => void; onDelete: () => void }) {
  const website = websites.find((w) => w.website.id === domain.website_id);
  return (
    <span className="topology__frontend-line">
      <span className="topology__glyph" aria-hidden="true">◇</span>
      <span>
        <strong>{domain.hostname}</strong>
        <span className="muted"> {labelFor(domainPurposeLabel, domain.purpose || domain.role)}{website ? ` · ${website.website.name || website.website.code}` : ''}</span>
        <DomainState domain={domain} />
      </span>
      <span className="pill-list">
        <button type="button" className="button button--ghost button--small" onClick={onEdit}>Edit</button>
        <button type="button" className="button button--danger-quiet button--small" onClick={onDelete}>Remove</button>
      </span>
    </span>
  );
}

// ---------------------------------------------------------------------------
// Health badge
// ---------------------------------------------------------------------------

function levelOf(status: SiteHealthEntry['status'] | string): 'ok' | 'warning' | 'critical' | 'unknown' {
  switch (status) {
    case 'healthy':
      return 'ok';
    case 'warning':
      return 'warning';
    case 'critical':
      return 'critical';
    default:
      return 'unknown';
  }
}

export function HealthBadge({ status, detail }: { status: SiteHealthEntry['status'] | string; detail?: string; small?: boolean }) {
  return <StatusBadge kind="siteHealth" value={status} title={detail} />;
}

// ---------------------------------------------------------------------------
// Hostnames section
// ---------------------------------------------------------------------------

function DomainsSection({
  view,
  environmentId,
  reloadToken,
  validation,
  busy,
  onValidate,
  onRequestCertificate,
  onRenewCertificate,
  onEdit,
  onDelete,
}: {
  view: MagentoTopologyView;
  environmentId: string;
  reloadToken: number;
  validation: DomainValidationResult[] | null;
  busy: string | null;
  onValidate: () => void;
  onRequestCertificate: () => void;
  onRenewCertificate: () => void;
  onEdit: (d: EnvironmentDomain) => void;
  onDelete: (d: EnvironmentDomain) => void;
}) {
  const all = [...view.websites.flatMap((w) => w.domains), ...view.unbound_domains];
  const websiteName = (id?: string) => view.websites.find((w) => w.website.id === id)?.website.name || view.websites.find((w) => w.website.id === id)?.website.code || '';
  const resultFor = (id: string) => validation?.find((r) => r.domain.id === id);
  return (
    <details className="topology__section" open={all.length > 0 && all.length <= 6}>
      <summary>
        Hostnames <span className="muted">({all.length})</span>
      </summary>
      <div className="topology__section-body">
        <div className="readiness__head">
          <CertificateAutomation environmentId={environmentId} reloadToken={reloadToken} />
          <div className="cert-actions">
            {needsCertificate(all) ? (
              <button type="button" className="button button--secondary button--small" onClick={onRequestCertificate} disabled={busy !== null} data-testid="domains-request-certificate">
                {busy === 'certificate' ? 'Requesting' : 'Request certificate'}
              </button>
            ) : null}
            {hasRenewableCertificate(all) ? (
              <button type="button" className="button button--secondary button--small" onClick={onRenewCertificate} disabled={busy !== null} data-testid="domains-renew-certificate">
                {busy === 'certificate' ? 'Renewing' : 'Renew certificate'}
              </button>
            ) : null}
            <button type="button" className="button button--secondary button--small" onClick={onValidate} disabled={busy !== null || all.length === 0} data-testid="domains-validate">
              {busy === 'validate' ? 'Checking' : 'Check all hostnames'}
            </button>
          </div>
        </div>
        {all.length === 0 ? (
          <p className="quiet-empty">No hostnames</p>
        ) : (
          <Table label="Hostnames" compact>
            <thead>
              <tr>
                <th scope="col">Hostname</th>
                <th scope="col">Certificate</th>
                <th scope="col">Website</th>
                <th scope="col">DNS</th>
                <th scope="col">HTTPS</th>
                <th scope="col">Verified</th>
                <th scope="col"><span className="visually-hidden">Actions</span></th>
              </tr>
            </thead>
            <tbody>
              {all.map((d) => {
                const r = resultFor(d.id);
                return (
                  <tr key={d.id} data-testid={`domain-row-${d.hostname}`}>
                    <td>
                      <code>{d.hostname}</code>
                      {!d.canonical || d.purpose === 'alias' ? <span className="muted"> → {all.find((x) => x.id === d.redirect_to_domain_id)?.hostname ?? 'canonical'}</span> : null}
                      <div className="table__sub">{labelFor(domainPurposeLabel, d.purpose || d.role)}</div>
                    </td>
                    <td><CertificateCell domain={d} /></td>
                    <td>{websiteName(d.website_id) || <span className="muted">Installation</span>}{d.store_view_id ? <span className="muted"> · {view.websites.flatMap((w) => w.store_views).find((v) => v.id === d.store_view_id)?.code}</span> : null}</td>
                    <td><StatusBadge kind="dns" value={d.dns_status} title={d.dns_observed} /></td>
                    <td>
                      <StatusBadge
                        kind="tls"
                        value={d.tls_observed_status}
                        title={d.tls_observed_not_after ? `until ${new Date(d.tls_observed_not_after).toLocaleDateString()}` : d.certificate_status !== 'none' ? `gate certificate: ${d.certificate_status}` : undefined}
                      />
                    </td>
                    <td>
                      <StatusBadge
                        kind="domainVerification"
                        value={d.verification_status}
                        title={d.verification_error || (r?.observation ? `${labelFor(redirectCheckLabel, r.observation.canonical_redirect ?? 'not_applicable')}` : undefined)}
                      />
                    </td>
                    <td>
                      <RowActions>
                        <button type="button" className="button button--secondary button--small" onClick={() => onEdit(d)} aria-label={`Edit ${d.hostname}`}>Edit</button>
                        <button type="button" className="button button--danger-quiet button--small" onClick={() => onDelete(d)} aria-label={`Remove ${d.hostname}`}>Remove</button>
                      </RowActions>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        )}
        {all.length > 0 ? <DnsRecords environmentId={environmentId} /> : null}
      </div>
    </details>
  );
}


// ---------------------------------------------------------------------------
// Frontends section
// ---------------------------------------------------------------------------

/**
 * Where the services of a Commerce Ops managed stack reach each other
 * (CO-123): what a storefront's server-side code, or Magento calling a
 * storefront, uses instead of a public hostname. Shown only when the server
 * says Commerce Ops runs this Magento; nothing is guessed otherwise.
 */
function InternalNetworkSection({ network }: { network: TopologyInternalNetwork }) {
  const rows: { key: string; what: string; detail: string; url: string }[] = [
    { key: 'graphql', what: 'Magento GraphQL', detail: 'For a storefront’s server-side requests', url: network.graphql_url },
    { key: 'rest', what: 'Magento REST', detail: 'Base of /rest/V1/… calls', url: network.rest_url },
    { key: 'magento', what: 'Magento', detail: `Service ${network.magento.service}, port ${network.magento.port}`, url: network.magento.url },
    ...network.frontends.map((f) => ({ key: f.application_id, what: f.name, detail: `Service ${f.service}, port ${f.port}`, url: f.url })),
  ];
  return (
    <details className="topology__section" data-testid="internal-network">
      <summary>
        Docker network <span className="muted">({rows.length} addresses)</span>
      </summary>
      <div className="topology__section-body">
        <p className="topology__text">
          Inside <code className="internal-network__url" translate="no">{network.network}</code> only; browsers cannot reach these addresses.
        </p>
        <Table label="Addresses inside the server’s network" compact className="internal-network__table">
          <thead>
            <tr>
              <th scope="col">Service</th>
              <th scope="col">Address</th>
              <th scope="col"><span className="visually-hidden">Copy</span></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.key}>
                <th scope="row">
                  {r.what}
                  <span className="internal-network__detail muted">{r.detail}</span>
                </th>
                <td><code className="internal-network__url" translate="no">{r.url}</code></td>
                <td className="internal-network__action"><CopyButton value={r.url} label={`${r.what} address`} /></td>
              </tr>
            ))}
            <tr>
              <th scope="row">
                Network name
                <span className="internal-network__detail muted">For a container you attach yourself</span>
              </th>
              <td><code className="internal-network__url" translate="no">{network.network}</code></td>
              <td className="internal-network__action"><CopyButton value={network.network} label="network name" /></td>
            </tr>
          </tbody>
        </Table>
      </div>
    </details>
  );
}

function FrontendsSection({ view, environmentId, onEdit }: { view: MagentoTopologyView; environmentId: string; onEdit: (fe: TopologyFrontendView) => void }) {
  const all = [...view.websites.flatMap((w) => w.frontends), ...view.unbound_frontends];
  if (all.length === 0) return null;
  return (
    <details className="topology__section">
      <summary>
        Frontend deployments <span className="muted">({all.length})</span>
      </summary>
      <div className="topology__section-body">
        {all.map((fe) => (
          <FrontendBindingRow key={fe.application.id} environmentId={environmentId} view={view} fe={fe} onEdit={() => onEdit(fe)} />
        ))}
      </div>
    </details>
  );
}

function FrontendBindingRow({ environmentId, view, fe, onEdit }: { environmentId: string; view: MagentoTopologyView; fe: TopologyFrontendView; onEdit: () => void }) {
  // Which variables carry these values is the component's environment
  // configuration, not the binding's business (CO-52): this row shows the
  // Magento context and how many of the component's variables read it.
  const config = useResource(() => api.getComponentEnvironment(environmentId, fe.application.id), [environmentId, fe.application.id, fe.binding?.updated_at]);
  const website = view.websites.find((w) => w.website.id === fe.binding?.website_id);
  const storeView = website?.store_views.find((v) => v.id === fe.binding?.store_view_id);
  const fromCommerceOps = (config.data?.environment.variables ?? []).filter((v) => v.source !== 'literal');
  return (
    <div className="topology__binding" data-testid={`frontend-binding-${fe.application.slug}`}>
      <div className="readiness__head">
        <div>
          <strong>{fe.application.name}</strong>
          <span className="muted">
            {' '}
            {website ? `${website.website.name || website.website.code}` : 'no website bound'}
            {storeView ? <> · store view <code>{storeView.code}</code></> : null}
            {fe.domains.length > 0 ? ` · ${fe.domains.map((d) => d.hostname).join(', ')}` : ''}
          </span>
        </div>
        <button type="button" className="button button--secondary button--small" onClick={onEdit}>
          {fe.binding ? 'Edit binding' : 'Bind to a website'}
        </button>
      </div>
      {fromCommerceOps.length > 0 ? (
        <ul className="topology__list">
          {fromCommerceOps.map((v) => (
            <li key={v.id}>
              <code>{v.name}</code> <span className="muted">— {v.source_label}</span>{' '}
              {v.resolved ? <code>{v.value}</code> : <StatusBadge kind="variableValue" value="missing" label={v.reason} />}
            </li>
          ))}
        </ul>
      ) : fe.binding && !config.loading ? (
        <p className="topology__text">
          No variable of {fe.application.name} reads this binding yet.
        </p>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Base URLs section
// ---------------------------------------------------------------------------

function BaseURLSection({ environmentId, reloadToken, busy, onApply }: { environmentId: string; reloadToken: number; busy: string | null; onApply: (plan: BaseURLPlan) => void }) {
  const plan = useResource(() => api.getMagentoBaseURLPlan(environmentId), [environmentId, reloadToken]);
  const [confirm, setConfirm] = useState(false);
  const p = plan.data?.plan;
  if (!p || p.in_sync) return null;
  return (
    <details className="topology__section">
      <summary>
        Base URLs <span className="badge badge--warning">{p.changes.length} change{p.changes.length === 1 ? '' : 's'} proposed</span>
      </summary>
      <div className="topology__section-body">
        <Table label="Magento base URLs by website" compact>
          <thead>
            <tr>
              <th scope="col">Setting</th>
              <th scope="col">Scope</th>
              <th scope="col">Current value</th>
              <th scope="col">Proposed value</th>
            </tr>
          </thead>
          <tbody>
            {p.changes.map((c) => (
              <tr key={`${c.scope}-${c.scope_code}-${c.path}`}>
                <td><code>{c.path}</code></td>
                <td>{c.target}<br /><span className="muted">--scope={c.scope} --scope-code={c.scope_code}</span></td>
                <td>
                  <code>{c.current_value || '—'}</code>
                  <br />
                  <span className="muted">from {c.current_scope} scope{c.current_scope_code ? ` (${c.current_scope_code})` : ''}</span>
                </td>
                <td><code>{c.proposed_value}</code></td>
              </tr>
            ))}
          </tbody>
        </Table>
        {p.warnings?.map((w) => (
          <Callout key={w} tone="warning">
            {w}
          </Callout>
        ))}
        <button type="button" className="button button--primary" onClick={() => setConfirm(true)} disabled={busy !== null}>
          Apply base URL changes
        </button>
        <ConfirmDialog
          open={confirm}
          title="Apply the base URL changes in Magento?"
          description={`Commerce Ops rewrites env.php with the ${p.changes.length} value(s) shown, at the website or store view scope of each, then flushes the cache. The default base URLs are locked in env.php, so Magento only honours per-website values from there; every deployment renders the same values. The change is recorded as an execution.`}
          confirmLabel="Apply changes"
          busy={busy === 'base-urls'}
          onConfirm={() => { setConfirm(false); onApply(p); }}
          onCancel={() => setConfirm(false)}
        />
      </div>
    </details>
  );
}

// ---------------------------------------------------------------------------
// Routing section
// ---------------------------------------------------------------------------

function RoutingSection({ environmentId, reloadToken, busy, onApply }: { environmentId: string; reloadToken: number; busy: string | null; onApply: () => void }) {
  const [open, setOpen] = useState(false);
  const [inspection, setInspection] = useState<RoutingInspection | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [loading, setLoading] = useState(false);
  const [confirm, setConfirm] = useState(false);
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    api.inspectStorefrontRouting(environmentId)
      .then((r) => { if (!cancelled) { setInspection(r.routing); setError(null); } })
      .catch((c: unknown) => { if (!cancelled) setError(c instanceof Error ? c : new Error(String(c))); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [environmentId, open, reloadToken]);
  return (
    <details className="topology__section" onToggle={(e) => setOpen((e.target as HTMLDetailsElement).open)}>
      <summary>Web server routing</summary>
      <div className="topology__section-body">
        {loading ? <LoadingRows label="Inspecting the server…" rows={2} rowHeight="32px" /> : null}
        <ErrorNotice error={error} />
        {inspection ? (
          <>
            <p>
              <strong>Ports 80/443 owned by:</strong> {labelFor(ingressOwnerLabel, inspection.owner)}
              {inspection.plan.strict_hosts ? <span className="badge">Unknown hostnames refused</span> : null}
            </p>
            <p className="muted">{inspection.note}</p>
            {inspection.proposed_nginx_map ? (
              <details>
                <summary>Proposed nginx mapping (MAGE_RUN_TYPE / MAGE_RUN_CODE)</summary>
                <pre className="topology__pre">{inspection.proposed_nginx_map}</pre>
              </details>
            ) : null}
            {inspection.proposed_apache ? (
              <details>
                <summary>Proposed Apache directives</summary>
                <pre className="topology__pre">{inspection.proposed_apache}</pre>
              </details>
            ) : null}
            {inspection.gate_config ? (
              <details>
                <summary>Generated gate configuration</summary>
                <pre className="topology__pre">{inspection.gate_config}</pre>
              </details>
            ) : null}
            {inspection.can_apply ? (
              <button type="button" className="button button--primary" onClick={() => setConfirm(true)} disabled={busy !== null}>
                Apply routing
              </button>
            ) : null}
            <ConfirmDialog
              open={confirm}
              title="Apply the routing to the Commerce Ops gate?"
              description="The generated gate and Magento vhost are rewritten, validated (openresty -t, nginx -t) and reloaded without dropping connections, then every routed hostname is checked. Web servers Commerce Ops does not own are never touched."
              confirmLabel="Apply routing"
              busy={busy === 'routing'}
              onConfirm={() => { setConfirm(false); onApply(); }}
              onCancel={() => setConfirm(false)}
            />
          </>
        ) : null}
      </div>
    </details>
  );
}

// ---------------------------------------------------------------------------
// Drift review dialog
// ---------------------------------------------------------------------------

function DriftReviewDialog({ open, discovery, drift, busy, onImport, onCancel }: { open: boolean; discovery: TopologyDiscovery | null; drift: TopologyDiff | null; busy: boolean; onImport: () => void; onCancel: () => void }) {
  const changes = drift?.changes ?? [];
  return (
    <Dialog
      open={open}
      size="lg"
      title="Magento websites: review before importing"
      description="Importing updates the record only; hostnames, frontends and Magento itself are not changed."
      onClose={onCancel}
      busy={busy}
      footer={
        <>
          <button type="button" className="button button--secondary" onClick={onCancel} disabled={busy}>
            Not now
          </button>
          <button type="button" className="button button--primary" onClick={onImport} disabled={busy || changes.length === 0}>
            {busy ? 'Importing' : 'Import into Commerce Ops'}
          </button>
        </>
      }
    >
      <p className="dialog__text">
        {discovery
          ? `Magento reports ${discovery.diff.websites_discovered} website(s)${discovery.discovered.partial ? ' (partial: some details could not be read)' : ''}.`
          : 'The latest discovery differs from what Commerce Ops has on record.'}
      </p>
      {discovery?.discovered.warnings?.length ? (
        <Callout tone="warning" title="Magento reported warnings">
          <ul className="callout__list">
            {discovery.discovered.warnings.map((w) => (
              <li key={w}>{w}</li>
            ))}
          </ul>
        </Callout>
      ) : null}
      {changes.length === 0 ? (
        <p>Nothing to import</p>
      ) : (
        <ul className="attention-list" data-testid="drift-changes">
          {changes.map((c, i) => <ChangeItem key={`${c.kind}-${c.subject}-${i}`} change={c} />)}
        </ul>
      )}
    </Dialog>
  );
}

function ChangeItem({ change }: { change: TopologyChange }) {
  return (
    <li className={`attention-list__item attention-list__item--${change.severity === 'info' ? 'info' : change.severity}`}>
      <div>
        <strong>{labelFor(topologyChangeLabel, change.kind)}: {change.subject}</strong>
        {change.expected || change.observed ? (
          <p className="attention-list__detail">
            {change.expected ? <>on record: <code>{change.expected}</code>{change.observed ? ' · ' : ''}</> : null}
            {change.observed ? <>Magento: <code>{change.observed}</code></> : null}
          </p>
        ) : null}
        {change.advice ? <p className="attention-list__detail">{change.advice}</p> : null}
      </div>
    </li>
  );
}

// ---------------------------------------------------------------------------
// Hostname dialog
// ---------------------------------------------------------------------------

function DomainDialog({
  view,
  frontends,
  domain,
  websiteId,
  onClose,
  onSave,
  busy,
  error,
}: {
  view: MagentoTopologyView;
  frontends: ProjectApplication[];
  domain?: EnvironmentDomain;
  websiteId?: string;
  onClose: () => void;
  onSave: (input: SetEnvironmentDomainInput) => void;
  busy: boolean;
  error: Error | null;
}) {
  const websites = view.websites.filter((w) => w.website.status === 'active');
  const single = websites.length === 1;
  const [hostname, setHostname] = useState(domain?.hostname ?? '');
  const [purpose, setPurpose] = useState<DomainPurpose>((domain?.purpose as DomainPurpose) || (frontends.length > 0 ? 'headless_storefront' : 'magento_storefront'));
  const [website, setWebsite] = useState(domain?.website_id ?? websiteId ?? (single ? websites[0]!.website.id : ''));
  const [storeView, setStoreView] = useState(domain?.store_view_id ?? '');
  const [frontend, setFrontend] = useState(domain?.application_id && frontends.some((f) => f.id === domain.application_id) ? domain.application_id : frontends[0]?.id ?? '');
  const [redirectTo, setRedirectTo] = useState(domain?.redirect_to_domain_id ?? '');
  const [tls, setTLS] = useState<TLSMode>(domain?.tls_mode ?? 'acme');
  const [redirectHTTPS, setRedirectHTTPS] = useState(domain?.redirect_to_https ?? true);
  const [acmeEmail, setAcmeEmail] = useState(domain?.acme_email ?? '');
  const views = websites.find((w) => w.website.id === website)?.store_views.filter((v) => v.status === 'active') ?? [];
  const candidates = [...view.websites.flatMap((w) => w.domains), ...view.unbound_domains].filter((d) => d.id !== domain?.id && d.purpose !== 'alias' && (!website || d.website_id === website || !d.website_id));
  const needsWebsite = purpose === 'magento_storefront' || purpose === 'headless_storefront' || purpose === 'alias';

  function submit(e: FormEvent) {
    e.preventDefault();
    const input: SetEnvironmentDomainInput = {
      hostname: hostname.trim().toLowerCase(),
      purpose,
      tls_mode: tls,
      redirect_to_https: redirectHTTPS,
      acme_email: acmeEmail.trim() || undefined,
      website_id: website || null,
      store_view_id: purpose === 'magento_storefront' && storeView ? storeView : null,
      application_id: purpose === 'headless_storefront' ? frontend || null : null,
      redirect_to_domain_id: purpose === 'alias' && redirectTo ? redirectTo : null,
      canonical: purpose !== 'alias',
    };
    onSave(input);
  }

  return (
    <FormDialog title={domain ? `Edit ${domain.hostname}` : 'Add a hostname'} onClose={onClose} onSubmit={submit} submitLabel={domain ? 'Save hostname' : 'Add hostname'} testId="domain-dialog" busy={busy} error={error}>
      <Field label="Hostname" hint="A bare hostname such as shop.example.com — no https://, no path.">
        <Input name="hostname" value={hostname} onChange={(e) => setHostname(e.target.value)} required disabled={Boolean(domain)} placeholder="shop.example.com" autoComplete="off" spellCheck={false} data-testid="domain-hostname" />
      </Field>
      <Field label="What is it for" hint={domainPurposeDescription[purpose]}>
        <Select value={purpose} onChange={(e) => setPurpose(e.target.value as DomainPurpose)} data-testid="domain-purpose">
          {(['magento_storefront', 'headless_storefront', 'backend_api', 'alias', 'admin', 'media'] as DomainPurpose[])
            .filter((p) => p !== 'headless_storefront' || frontends.length > 0)
            .map((p) => <option key={p} value={p}>{domainPurposeLabel[p]}</option>)}
        </Select>
      </Field>
      {needsWebsite && websites.length > 0 ? (
        <Field label="Magento website" hint={single ? 'This installation has one website.' : 'Which website this hostname belongs to. Required when the installation has several websites.'}>
          <Select value={website} onChange={(e) => { setWebsite(e.target.value); setStoreView(''); }} required={view.multi_website} data-testid="domain-website">
            {!single ? <option value="">Choose a website</option> : null}
            {websites.map((w) => <option key={w.website.id} value={w.website.id}>{w.website.name || w.website.code} ({w.website.code})</option>)}
          </Select>
        </Field>
      ) : null}
      {purpose === 'magento_storefront' && views.length > 1 ? (
        <Field label="Store view (optional)" hint="Leave empty to route by website (its default store view answers). Choose one to route this hostname to a specific store view, for example a language.">
          <Select value={storeView} onChange={(e) => setStoreView(e.target.value)}>
            <option value="">Website default</option>
            {views.map((v) => <option key={v.id} value={v.id}>{v.name} ({v.code}{v.locale ? `, ${v.locale}` : ''})</option>)}
          </Select>
        </Field>
      ) : null}
      {purpose === 'headless_storefront' ? (
        <Field label="Frontend deployment" hint="The deployment that answers on this hostname.">
          <Select value={frontend} onChange={(e) => setFrontend(e.target.value)} required>
            {frontends.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
          </Select>
        </Field>
      ) : null}
      {purpose === 'alias' ? (
        <Field label="Redirects to" hint="The canonical hostname of the same website. Loops are refused.">
          <Select value={redirectTo} onChange={(e) => setRedirectTo(e.target.value)} required>
            <option value="">Choose a hostname</option>
            {candidates.map((d) => <option key={d.id} value={d.id}>{d.hostname}</option>)}
          </Select>
        </Field>
      ) : null}
      <Field label="HTTPS" hint={tls === 'acme' ? "Commerce Ops obtains a Let's Encrypt certificate on its gate." : tls === 'external' ? 'A CDN or load balancer in front terminates HTTPS (for example Cloudflare, SSL mode Full).' : 'Plain HTTP only. For laboratories.'}>
        <Select value={tls} onChange={(e) => setTLS(e.target.value as TLSMode)}>
          <option value="acme">Let's Encrypt on the Commerce Ops gate</option>
          <option value="external">Terminated in front (CDN, load balancer)</option>
          <option value="none">None (HTTP only)</option>
        </Select>
      </Field>
      {tls !== 'none' ? (
        <Checkbox
          checked={redirectHTTPS}
          onChange={(e) => setRedirectHTTPS(e.target.checked)}
          label="Redirect plain HTTP to HTTPS once a certificate is installed"
        />
      ) : null}
      {tls === 'acme' ? (
        <Field label="Certificate contact e-mail (optional)" hint="Let's Encrypt expiry notices go here.">
          <Input type="email" name="acme_email" value={acmeEmail} onChange={(e) => setAcmeEmail(e.target.value)} autoComplete="email" />
        </Field>
      ) : null}
    </FormDialog>
  );
}

// ---------------------------------------------------------------------------
// Frontend binding dialog
// ---------------------------------------------------------------------------

/**
 * The GraphQL endpoints this environment is known to have (CO-123): the
 * internal address on the managed stack's Docker network, and the public
 * API hostname. The canonical backend-API hostname record wins over the
 * runtime configuration's URL, as for the GraphQL monitors.
 */
export function graphqlEndpointChoices(view: MagentoTopologyView): { key: string; title: string; url: string; why: string }[] {
  const out: { key: string; title: string; url: string; why: string }[] = [];
  if (view.internal_network) {
    out.push({ key: 'internal', title: 'Internal', url: view.internal_network.graphql_url, why: 'Server-side requests from this storefront’s container stay on the server.' });
  }
  const allDomains = [...view.unbound_domains, ...view.websites.flatMap((w) => w.domains)];
  const api = allDomains.find((d) => d.purpose === 'backend_api' && d.canonical);
  let publicURL = '';
  if (api) {
    publicURL = `${api.tls_mode === 'none' ? 'http' : 'https'}://${api.hostname}/graphql`;
  } else {
    const base = String(view.installation.backend_api_url ?? '').trim().replace(/\/+$/, '').replace(/\/graphql$/, '');
    if (base) publicURL = `${base}/graphql`;
  }
  if (publicURL) {
    out.push({ key: 'public', title: 'Public', url: publicURL, why: 'Reachable from anywhere, including visitors’ browsers.' });
  }
  return out;
}

function FrontendBindingDialog({ view, frontend, websiteId, onClose, onSave, busy, error }: { view: MagentoTopologyView; frontend: TopologyFrontendView; websiteId?: string; onClose: () => void; onSave: (input: SetFrontendBindingInput) => void; busy: boolean; error: Error | null }) {
  const websites = view.websites.filter((w) => w.website.status === 'active');
  const b = frontend.binding;
  const [website, setWebsite] = useState(b?.website_id ?? websiteId ?? (websites.length === 1 ? websites[0]!.website.id : ''));
  const [storeView, setStoreView] = useState(b?.store_view_id ?? '');
  const [graphql, setGraphql] = useState(b?.graphql_endpoint ?? String(view.installation.backend_api_url ?? ''));
  const [publicURL, setPublicURL] = useState(b?.public_base_url ?? (frontend.domains[0] ? `https://${frontend.domains[0].hostname}/` : ''));
  const [media, setMedia] = useState<FrontendMagentoBinding['media_url_strategy']>(b?.media_url_strategy ?? 'magento');
  const views = websites.find((w) => w.website.id === website)?.store_views.filter((v) => v.status === 'active' && v.is_active) ?? [];
  const endpoints = graphqlEndpointChoices(view);

  function submit(e: FormEvent) {
    e.preventDefault();
    onSave({
      application_id: frontend.application.id,
      website_id: website || null,
      store_view_id: storeView || null,
      graphql_endpoint: graphql.trim(),
      public_base_url: publicURL.trim(),
      media_url_strategy: media,
    });
  }

  return (
    <FormDialog title={`${frontend.application.name}: Magento context`} onClose={onClose} onSubmit={submit} submitLabel="Save binding" testId="binding-dialog" busy={busy} error={error}>
      <Field label="Magento website" hint="The website whose catalog and configuration this frontend shows.">
        <Select value={website} onChange={(e) => { setWebsite(e.target.value); setStoreView(''); }} required data-testid="binding-website">
          <option value="">Choose a website</option>
          {websites.map((w) => <option key={w.website.id} value={w.website.id}>{w.website.name || w.website.code} ({w.website.code})</option>)}
        </Select>
      </Field>
      <Field label="Store view" hint="Sent as the GraphQL Store header. Empty means the website's default store view.">
        <Select value={storeView} onChange={(e) => setStoreView(e.target.value)} data-testid="binding-store-view">
          <option value="">Website default</option>
          {views.map((v) => <option key={v.id} value={v.id}>{v.name} ({v.code}{v.locale ? `, ${v.locale}` : ''})</option>)}
        </Select>
      </Field>
      <Field label="Magento GraphQL endpoint" hint="Where this frontend calls Magento.">
        <Input type="url" name="graphql_endpoint" value={graphql} onChange={(e) => setGraphql(e.target.value)} placeholder="https://magento.example.com/graphql" autoComplete="off" spellCheck={false} />
      </Field>
      {endpoints.length > 0 ? (
        <ul className="endpoint-choices" aria-label="Known GraphQL endpoints" data-testid="binding-endpoint-choices">
          {endpoints.map((c) => {
            const inUse = graphql.trim() === c.url;
            return (
              <li key={c.key} className="endpoint-choice">
                <div className="endpoint-choice__text">
                  <span className="endpoint-choice__title">{c.title}</span>
                  <code className="endpoint-choice__url" translate="no">{c.url}</code>
                  <span className="endpoint-choice__why">{c.why}</span>
                </div>
                <button type="button" className="button button--secondary button--small" onClick={() => setGraphql(c.url)} disabled={inUse} aria-label={inUse ? `In use: ${c.title.toLowerCase()} address` : `Use this ${c.title.toLowerCase()} address`}>
                  {inUse ? 'In use' : 'Use this'}
                </button>
              </li>
            );
          })}
        </ul>
      ) : null}
      <Field label="Public base URL" hint="What visitors type. Usually the canonical hostname bound to this frontend.">
        <Input type="url" name="public_base_url" value={publicURL} onChange={(e) => setPublicURL(e.target.value)} placeholder="https://shop.example.com/" autoComplete="off" spellCheck={false} />
      </Field>
      <Field label="Product images and media" hint="Where the frontend loads media from.">
        <Select value={media} onChange={(e) => setMedia(e.target.value as FrontendMagentoBinding['media_url_strategy'])}>
          <option value="magento">Directly from Magento's media URL</option>
          <option value="frontend">Proxied or rewritten by the frontend</option>
          <option value="cdn">From a CDN</option>
        </Select>
      </Field>
      <p className="topology__text">
        Variables are configured under Repositories → {frontend.application.name} → Environment variables.
      </p>
    </FormDialog>
  );
}

// ---------------------------------------------------------------------------
// A form in the shared Dialog
// ---------------------------------------------------------------------------

function FormDialog({ title, children, onClose, onSubmit, submitLabel, testId, busy = false, error = null }: { title: string; children: ReactNode; onClose: () => void; onSubmit: (e: FormEvent) => void; submitLabel: string; testId?: string; busy?: boolean; error?: Error | null }) {
  return (
    <Dialog
      open
      size="md"
      title={title}
      onClose={onClose}
      busy={busy}
      onSubmit={onSubmit}
      testId={testId}
      footer={
        <>
          <button type="button" className="button button--secondary" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="submit" className="button button--primary" disabled={busy}>
            {busy ? 'Saving' : submitLabel}
          </button>
        </>
      }
    >
      {children}
      <ErrorNotice error={error} />
    </Dialog>
  );
}

function ago(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const minutes = Math.round(diff / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 48) return `${hours} h ago`;
  return `${Math.round(hours / 24)} days ago`;
}
